import { useEffect, useState, type ReactNode } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
  Grid, IconButton, MenuItem, Stack, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Tooltip, Typography,
} from '@mui/material';
import { AddRounded, CloseRounded, EditOutlined, LockOutlined } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  companySetupApi, type BankAccount, type DocumentTemplate, type IfscLookup, type NumberSeries, type SaveBankAccount,
} from '@/api/companySetup.api';
import Can from '@/components/common/Can';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import DataState from '@/components/common/DataState';
import IfscField from '@/components/common/IfscField';
import { useNotify } from '@/components/common/useNotify';
import { useBranding } from '@/hooks/usePublicBranding';
import { humanizeError } from '@/utils/errorMessage';

const ACCOUNT_TYPES: { value: BankAccount['accountType']; label: string }[] = [
  { value: 'Current', label: 'Current' },
  { value: 'Savings', label: 'Savings' },
  { value: 'CashCredit', label: 'Cash credit' },
  { value: 'Overdraft', label: 'Overdraft' },
];

function SectionCard({ title, description, action, children }: { title: string; description?: string; action?: ReactNode; children: ReactNode }) {
  return (
    <Card>
      <CardContent sx={{ p: { xs: 2, sm: 2.5 }, '&:last-child': { pb: { xs: 2, sm: 2.5 } } }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'flex-start' }} spacing={1} sx={{ mb: 2 }}>
          <Box>
            <Typography variant="subtitle1" component="h2" fontWeight={700}>{title}</Typography>
            {description && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>{description}</Typography>}
          </Box>
          {action}
        </Stack>
        {children}
      </CardContent>
    </Card>
  );
}

// =============================================================================
// Bank accounts (guided setup step "Bank account")
// =============================================================================

const BANK_KEY = ['company-setup', 'bank-accounts'] as const;

export function BankAccountsSection() {
  const qc = useQueryClient();
  const notify = useNotify();
  const list = useQuery({ queryKey: BANK_KEY, queryFn: companySetupApi.bankAccounts });
  const [editing, setEditing] = useState<BankAccount | 'new' | null>(null);
  const [deleting, setDeleting] = useState<BankAccount | null>(null);
  const refresh = () => { qc.invalidateQueries({ queryKey: BANK_KEY }); qc.invalidateQueries({ queryKey: ['setup-guide'] }); };
  const remove = useMutation({ mutationFn: (id: number) => companySetupApi.deleteBankAccount(id), onSuccess: refresh });

  return (
    <DataState loading={list.isLoading} error={list.error} onRetry={list.refetch} loadingText="Loading bank accounts…">
      <SectionCard
        title="Bank accounts"
        description="The accounts customers pay into. The primary account is printed on quotes, invoices and payment requests."
        action={
          <Can perm="Company.Edit">
            <Button variant="contained" startIcon={<AddRounded />} onClick={() => setEditing('new')}>Add account</Button>
          </Can>
        }
      >
        {(list.data ?? []).length === 0 ? (
          <Alert severity="info">No bank account yet. Add the account customers pay into.</Alert>
        ) : (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 640 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Bank</TableCell>
                  <TableCell>Account</TableCell>
                  <TableCell>IFSC / SWIFT</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(list.data ?? []).map((a) => (
                  <TableRow key={a.id} hover sx={{ opacity: a.isActive ? 1 : 0.55 }}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{a.bankName}</Typography>
                      <Typography variant="caption" color="text.secondary">{a.accountHolder}{a.bankBranch ? ` · ${a.bankBranch}` : ''}</Typography>
                    </TableCell>
                    <TableCell>
                      {a.accountNumberMasked} {a.isPrimary && <Chip size="small" color="primary" label="Primary" sx={{ ml: 0.5 }} />}
                    </TableCell>
                    <TableCell>{a.ifsc ?? a.swift ?? '—'}</TableCell>
                    <TableCell>{ACCOUNT_TYPES.find((t) => t.value === a.accountType)?.label ?? a.accountType} · {a.currency}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Can perm="Company.Edit">
                        <Tooltip title="Edit"><IconButton size="small" onClick={() => setEditing(a)}><EditOutlined fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleting(a)}><CloseRounded fontSize="small" /></IconButton></Tooltip>
                      </Can>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </SectionCard>

      {editing !== null && (
        <BankAccountDialog account={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onSaved={refresh} />
      )}

      <ConfirmDialog
        open={deleting !== null}
        title="Delete bank account?"
        message={`${deleting?.bankName ?? ''} ${deleting?.accountNumberMasked ?? ''} will no longer be printed on quotes and invoices.`}
        confirmLabel="Yes, delete"
        destructive
        loading={remove.isPending}
        onConfirm={async () => {
          if (!deleting) return;
          try { await remove.mutateAsync(deleting.id); notify.success('Bank account deleted.'); setDeleting(null); } catch (e) { notify.error(e); }
        }}
        onClose={() => setDeleting(null)}
      />
    </DataState>
  );
}

function BankAccountDialog({ account, onClose, onSaved }: { account: BankAccount | null; onClose: () => void; onSaved: () => void }) {
  const notify = useNotify();
  const [v, setV] = useState<SaveBankAccount>({
    accountHolder: account?.accountHolder ?? '',
    bankName: account?.bankName ?? '',
    bankBranch: account?.bankBranch ?? '',
    accountNumber: '',
    confirmAccountNumber: '',
    ifsc: account?.ifsc ?? '',
    swift: account?.swift ?? '',
    accountType: account?.accountType ?? 'Current',
    currency: account?.currency ?? 'INR',
    isPrimary: account?.isPrimary ?? false,
    isActive: account?.isActive ?? true,
  });
  const [error, setError] = useState<string | null>(null);
  const set = (patch: Partial<SaveBankAccount>) => setV((p) => ({ ...p, ...patch }));

  // IFSC → bank and branch from the bank master as soon as all 11 characters are typed. The full IFSC gives both
  // (fixed); the first 4 letters alone give only the bank, and the branch is typed; a retired IFSC cannot be saved.
  const [ifscCheck, setIfscCheck] = useState<{ code: string; result: IfscLookup | null; checking: boolean } | null>(null);
  useEffect(() => {
    const code = v.ifsc ?? '';
    if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(code)) { setIfscCheck(null); return; }
    let live = true;
    setIfscCheck({ code, result: null, checking: true });
    companySetupApi.bankIfsc(code)
      .then((r) => {
        if (!live) return;
        setIfscCheck({ code, result: r, checking: false });
        if (r.found && r.details) setV((p) => ({ ...p, bankName: r.details!.bank.toUpperCase(), bankBranch: (r.details!.branch ?? p.bankBranch ?? '').toUpperCase() }));
        else if (r.prefixBank) setV((p) => ({ ...p, bankName: r.prefixBank!.toUpperCase() }));
      })
      .catch(() => { if (live) setIfscCheck({ code, result: null, checking: false }); });
    return () => { live = false; };
  }, [v.ifsc]);
  const ifscResult = ifscCheck?.result ?? null;
  const bankFromIfsc = !!ifscResult && (ifscResult.found || !!ifscResult.prefixBank);
  const branchFromIfsc = !!ifscResult?.found && !!ifscResult.details?.branch;
  const ifscNote = ifscCheck?.checking ? 'Checking the IFSC…'
    : ifscResult?.found && ifscResult.details ? `From IFSC: ${[ifscResult.details.bank, ifscResult.details.branch, ifscResult.details.city, ifscResult.details.state].filter(Boolean).join(', ')}`
    : ifscResult?.error ?? null;
  const ifscBlocked = !!ifscResult?.retired;

  const save = useMutation({
    mutationFn: () => (account ? companySetupApi.updateBankAccount(account.id, v) : companySetupApi.addBankAccount(v)),
  });

  // The same rules the server applies, shown under each field as it is typed.
  const mismatch = !!v.accountNumber && !!v.confirmAccountNumber && v.accountNumber !== v.confirmAccountNumber;
  const accountErr = v.accountNumber && !/^[A-Za-z0-9]{6,34}$/.test(v.accountNumber) ? '6 to 34 letters or digits, no spaces or symbols.' : null;
  const ifscErr = v.ifsc && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(v.ifsc) ? '11 characters: 4 letters, a zero, then 6 letters or digits (e.g. HDFC0001234).' : null;
  const swiftErr = v.swift && !/^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(v.swift) ? '8 or 11 characters (e.g. HDFCINBB).' : null;
  const currencyErr = v.currency && !/^[A-Z]{3}$/.test(v.currency) ? '3 letters, e.g. INR.' : null;
  const needsCode = !v.ifsc?.trim() && !v.swift?.trim();
  const canSave = v.accountHolder.trim() && v.bankName.trim() && !needsCode && !ifscBlocked && !ifscCheck?.checking
    && (account || (v.accountNumber && v.confirmAccountNumber)) && !mismatch && !accountErr && !ifscErr && !swiftErr && !currencyErr;

  const doSave = async () => {
    setError(null);
    try { await save.mutateAsync(); notify.success(account ? 'Bank account saved.' : 'Bank account added.'); onSaved(); onClose(); }
    catch (e) { setError(humanizeError(e, 'Could not save the bank account.')); }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{account ? 'Edit bank account' : 'Add bank account'}</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Grid container spacing={1.5}>
          <Grid size={12}>
            <TextField fullWidth size="small" required label="Name on the account" value={v.accountHolder}
              onChange={(e) => set({ accountHolder: e.target.value.toUpperCase() })} />
          </Grid>
          <Grid size={12}>
            <IfscField label="IFSC (India)" value={v.ifsc ?? ''} search={companySetupApi.ifscSearch}
              error={!!ifscErr || ifscBlocked || (!!ifscResult && !ifscResult.found && !ifscResult.prefixBank)}
              helperText={ifscErr ?? ifscNote ?? (needsCode ? 'Enter the IFSC (India) or the SWIFT code (abroad).' : 'Type the bank code (e.g. SBIN) and pick the branch.')}
              onChange={(code) => set({ ifsc: code })}
              onPick={(b) => set({ ifsc: b.ifsc, bankName: b.bank.toUpperCase(), bankBranch: (b.branch ?? '').toUpperCase() })} />
          </Grid>
          <Grid size={12}>
            <TextField fullWidth size="small" required label="Bank" value={v.bankName} disabled={bankFromIfsc} helperText={bankFromIfsc ? 'From the IFSC.' : ' '}
              onChange={(e) => set({ bankName: e.target.value.toUpperCase() })} />
          </Grid>
          <Grid size={12}>
            <TextField fullWidth size="small" label="Bank branch" value={v.bankBranch ?? ''} disabled={branchFromIfsc}
              helperText={branchFromIfsc ? 'From the IFSC.' : ifscResult?.prefixBank ? 'Branch not in the RBI list: type it.' : ' '}
              onChange={(e) => set({ bankBranch: e.target.value.toUpperCase() })} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField fullWidth size="small" required={!account} label="Account number" type="password" autoComplete="off"
              error={!!accountErr}
              helperText={accountErr ?? (account ? `Stored: ${account.accountNumberMasked}. Leave blank to keep it.` : 'Typed twice to catch mistakes.')}
              value={v.accountNumber ?? ''} onChange={(e) => set({ accountNumber: e.target.value.replace(/\s/g, '') })} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField fullWidth size="small" required={!account || !!v.accountNumber} label="Account number again" autoComplete="off"
              error={mismatch} helperText={mismatch ? 'The two numbers do not match.' : ' '}
              value={v.confirmAccountNumber ?? ''} onChange={(e) => set({ confirmAccountNumber: e.target.value.replace(/\s/g, '') })} />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField fullWidth size="small" label="SWIFT (abroad)" value={v.swift ?? ''} error={!!swiftErr} helperText={swiftErr ?? ' '} slotProps={{ htmlInput: { maxLength: 11 } }}
              onChange={(e) => set({ swift: e.target.value.toUpperCase().replace(/\s/g, '') })} />
          </Grid>
          <Grid size={{ xs: 8, sm: 4 }}>
            <TextField fullWidth size="small" select label="Account type" value={v.accountType}
              onChange={(e) => set({ accountType: e.target.value as BankAccount['accountType'] })}>
              {ACCOUNT_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid size={{ xs: 4, sm: 4 }}>
            <TextField fullWidth size="small" label="Currency" value={v.currency ?? ''} error={!!currencyErr} helperText={currencyErr ?? ' '} slotProps={{ htmlInput: { maxLength: 3 } }}
              onChange={(e) => set({ currency: e.target.value.toUpperCase() })} />
          </Grid>
        </Grid>
        <Stack direction="row" spacing={2} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
          <FormControlLabel control={<Switch checked={v.isPrimary} onChange={(e) => set({ isPrimary: e.target.checked })} />} label="Primary (printed on documents)" />
          <FormControlLabel control={<Switch checked={v.isActive} onChange={(e) => set({ isActive: e.target.checked })} />} label="In use" />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={doSave} disabled={save.isPending || !canSave}>{save.isPending ? 'Saving…' : 'Save'}</Button>
      </DialogActions>
    </Dialog>
  );
}

// =============================================================================
// Invoice series (ACC-SER)
// =============================================================================

const SERIES_KEY = ['company-setup', 'number-series'] as const;

const DOC_LABEL: Record<string, string> = {
  Invoice: 'Invoice', CreditNote: 'Credit note', DebitNote: 'Debit note', ReceiptVoucher: 'Receipt voucher', Proforma: 'Proforma invoice', Quote: 'Quote',
};
const CATEGORY_LABEL: Record<string, string> = {
  TourPackage: 'Tour package', ProfitBasis: 'Profit basis', Online: 'Online sale', BillOfSupply: 'Bill of supply',
};

export function InvoiceSeriesSection() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: SERIES_KEY, queryFn: companySetupApi.series });
  const [editing, setEditing] = useState<NumberSeries | null>(null);
  const [adding, setAdding] = useState(false);
  const refresh = () => { qc.invalidateQueries({ queryKey: SERIES_KEY }); qc.invalidateQueries({ queryKey: ['setup-guide'] }); };

  const rows = list.data ?? [];
  const groups = Array.from(new Set(rows.map((s) => s.salesCategory)));

  return (
    <DataState loading={list.isLoading} error={list.error} onRetry={list.refetch} loadingText="Loading series…">
      <SectionCard
        title="Invoice series"
        description="Each sales category numbers its invoices, credit notes and debit notes separately; numbers restart every fiscal year. A GST number has at most 16 letters, digits, - and /."
        action={
          <Can perm="Company.Edit">
            <Button variant="outlined" startIcon={<AddRounded />} onClick={() => setAdding(true)}>Add sales category</Button>
          </Can>
        }
      >
        {rows.length > 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Fiscal year {rows[0].fiscalYear}{rows[0].gstin ? ` · GSTIN ${rows[0].gstin}` : ''}
          </Typography>
        )}
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 600 }}>
            <TableHead>
              <TableRow>
                <TableCell>Category</TableCell>
                <TableCell>Document</TableCell>
                <TableCell>Next number</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.flatMap((g) => rows.filter((s) => s.salesCategory === g).map((s, i) => (
                <TableRow key={s.id} hover sx={{ opacity: s.isActive ? 1 : 0.55 }}>
                  <TableCell sx={{ fontWeight: 600 }}>{i === 0 ? (CATEGORY_LABEL[g] ?? (g || 'Other documents')) : ''}</TableCell>
                  <TableCell>{DOC_LABEL[s.documentType] ?? s.documentType}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{s.sample}</TableCell>
                  <TableCell>
                    {s.locked ? <Chip size="small" icon={<LockOutlined />} label="In use" /> : s.isActive ? <Chip size="small" color="success" variant="outlined" label="Active" /> : <Chip size="small" label="Off" />}
                  </TableCell>
                  <TableCell align="right">
                    <Can perm="Company.Edit">
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => setEditing(s)}><EditOutlined fontSize="small" /></IconButton></Tooltip>
                    </Can>
                  </TableCell>
                </TableRow>
              )))}
            </TableBody>
          </Table>
        </TableContainer>
      </SectionCard>

      {editing && <SeriesDialog series={editing} onClose={() => setEditing(null)} onSaved={refresh} />}
      {adding && <CategoryDialog onClose={() => setAdding(false)} onSaved={refresh} />}
    </DataState>
  );
}

function SeriesDialog({ series, onClose, onSaved }: { series: NumberSeries; onClose: () => void; onSaved: () => void }) {
  const notify = useNotify();
  const [prefix, setPrefix] = useState(series.prefix);
  const [padding, setPadding] = useState(series.padding);
  const [next, setNext] = useState(series.nextNumber);
  const [active, setActive] = useState(series.isActive);
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({ mutationFn: () => companySetupApi.updateSeries(series.id, { prefix, padding, nextNumber: next, isActive: active }) });
  const sample = prefix + String(next).padStart(padding, '0');
  const tooLong = sample.length > 16;

  const doSave = async () => {
    setError(null);
    try { await save.mutateAsync(); notify.success('Series saved.'); onSaved(); onClose(); }
    catch (e) { setError(humanizeError(e, 'Could not save the series.')); }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{DOC_LABEL[series.documentType] ?? series.documentType} series</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {series.locked && <Alert severity="info" sx={{ mb: 2 }}>Documents have been numbered from this series, so only switching it on or off is possible.</Alert>}
        <Stack spacing={1.5}>
          <TextField size="small" label="Prefix" value={prefix} disabled={series.locked}
            onChange={(e) => setPrefix(e.target.value.toUpperCase().replace(/[^A-Z0-9/-]/g, ''))} />
          <Stack direction="row" spacing={1.5}>
            <TextField size="small" type="number" label="Digits" value={padding} disabled={series.locked}
              slotProps={{ htmlInput: { min: 1, max: 8 } }} onChange={(e) => setPadding(Math.max(1, Math.min(8, Number(e.target.value) || 1)))} />
            <TextField size="small" type="number" label="Next number" value={next} disabled={series.locked}
              slotProps={{ htmlInput: { min: 1 } }} onChange={(e) => setNext(Math.max(1, Number(e.target.value) || 1))} />
          </Stack>
          <Typography variant="body2" color={tooLong ? 'error' : 'text.secondary'}>
            Next document: <strong style={{ fontFamily: 'monospace' }}>{sample}</strong>{tooLong ? ` — ${sample.length} characters, at most 16 allowed.` : ''}
          </Typography>
          <FormControlLabel control={<Switch checked={active} onChange={(e) => setActive(e.target.checked)} />} label="Active" />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={doSave} disabled={save.isPending || tooLong || !prefix}>{save.isPending ? 'Saving…' : 'Save'}</Button>
      </DialogActions>
    </Dialog>
  );
}

function CategoryDialog({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const notify = useNotify();
  const [v, setV] = useState({ category: '', invoiceCode: '', creditNoteCode: '', debitNoteCode: '' });
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({ mutationFn: () => companySetupApi.addCategory(v) });
  const code = (s: string) => s.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 4);
  const ok = v.category.trim() && v.invoiceCode && v.creditNoteCode && v.debitNoteCode;

  const doSave = async () => {
    setError(null);
    try { await save.mutateAsync(); notify.success('Sales category added.'); onSaved(); onClose(); }
    catch (e) { setError(humanizeError(e, 'Could not add the category.')); }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Add sales category</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Stack spacing={1.5}>
          <TextField size="small" label="Category (e.g. B2B agent sales)" value={v.category} onChange={(e) => setV({ ...v, category: e.target.value })} />
          <TextField size="small" label="Invoice code (e.g. B2B)" value={v.invoiceCode} onChange={(e) => setV({ ...v, invoiceCode: code(e.target.value) })} />
          <TextField size="small" label="Credit note code (e.g. CNA)" value={v.creditNoteCode} onChange={(e) => setV({ ...v, creditNoteCode: code(e.target.value) })} />
          <TextField size="small" label="Debit note code (e.g. DNA)" value={v.debitNoteCode} onChange={(e) => setV({ ...v, debitNoteCode: code(e.target.value) })} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={doSave} disabled={save.isPending || !ok}>{save.isPending ? 'Adding…' : 'Add'}</Button>
      </DialogActions>
    </Dialog>
  );
}

// =============================================================================
// Quote, invoice and voucher templates (FR-PLT-04)
// =============================================================================

const TEMPLATE_KEY = ['company-setup', 'document-templates'] as const;
const DESIGNS: DocumentTemplate['design'][] = ['Classic', 'Modern', 'Minimal'];
const TEMPLATE_LABEL: Record<DocumentTemplate['documentType'], string> = { Quote: 'Quote', Invoice: 'Invoice', Voucher: 'Voucher' };

export function DocumentTemplatesSection() {
  const list = useQuery({ queryKey: TEMPLATE_KEY, queryFn: companySetupApi.templates });
  return (
    <DataState loading={list.isLoading} error={list.error} onRetry={list.refetch} loadingText="Loading templates…">
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          Choose how your quotes, invoices and vouchers look. Each uses your logo and colours from Branding.
        </Typography>
        {(list.data ?? []).map((t) => <TemplateCard key={t.documentType} template={t} />)}
      </Stack>
    </DataState>
  );
}

function TemplateCard({ template }: { template: DocumentTemplate }) {
  const qc = useQueryClient();
  const notify = useNotify();
  const [v, setV] = useState(template);
  useEffect(() => setV(template), [template]);
  const save = useMutation({ mutationFn: () => companySetupApi.updateTemplate(v), onSuccess: () => qc.invalidateQueries({ queryKey: TEMPLATE_KEY }) });
  const dirty = JSON.stringify(v) !== JSON.stringify(template);

  const doSave = async () => {
    try { await save.mutateAsync(); notify.success(`${TEMPLATE_LABEL[v.documentType]} template saved.`); } catch (e) { notify.error(e); }
  };

  return (
    <SectionCard title={`${TEMPLATE_LABEL[v.documentType]} template`}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 7 }}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
            {DESIGNS.map((d) => (
              <Chip key={d} label={d} clickable color={v.design === d ? 'primary' : 'default'} variant={v.design === d ? 'filled' : 'outlined'}
                onClick={() => setV({ ...v, design: d })} />
            ))}
          </Stack>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <FormControlLabel control={<Switch checked={v.showLogo} onChange={(e) => setV({ ...v, showLogo: e.target.checked })} />} label="Show logo" />
            <FormControlLabel control={<Switch checked={v.useBrandColours} onChange={(e) => setV({ ...v, useBrandColours: e.target.checked })} />} label="Use brand colours" />
          </Stack>
          <TextField fullWidth size="small" multiline minRows={2} sx={{ mt: 1.5 }} label="Footer text" placeholder="e.g. Subject to our terms and conditions."
            value={v.footerText ?? ''} onChange={(e) => setV({ ...v, footerText: e.target.value })} slotProps={{ htmlInput: { maxLength: 500 } }} />
          <Can perm="Company.Edit">
            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1.5 }}>
              <Button variant="contained" onClick={doSave} disabled={!dirty || save.isPending}>{save.isPending ? 'Saving…' : 'Save'}</Button>
            </Stack>
          </Can>
        </Grid>
        <Grid size={{ xs: 12, md: 5 }}>
          <TemplatePreview template={v} />
        </Grid>
      </Grid>
    </SectionCard>
  );
}

/** A small sketch of the page: where the logo, colour band and footer sit in each design. */
function TemplatePreview({ template }: { template: DocumentTemplate }) {
  const { companyName, primaryColor } = useBranding();
  const colour = template.useBrandColours ? (primaryColor ?? '#1D4E89') : '#334155';
  const line = (w: string, o = 0.18) => <Box sx={{ height: 6, width: w, bgcolor: '#0F172A', opacity: o, borderRadius: 1, mb: 0.75 }} />;
  const header = template.design === 'Modern'
    ? <Box sx={{ bgcolor: colour, color: '#fff', p: 1.25, fontSize: 11, fontWeight: 700 }}>{template.showLogo && '▣ '}{companyName}</Box>
    : template.design === 'Minimal'
      ? <Box sx={{ p: 1.25, fontSize: 11, fontWeight: 700, color: colour }}>{template.showLogo && '▣ '}{companyName}</Box>
      : <Box sx={{ p: 1.25, fontSize: 11, fontWeight: 700, borderBottom: `3px solid ${colour}` }}>{template.showLogo && <span style={{ color: colour }}>▣ </span>}{companyName}</Box>;

  return (
    <Box aria-label={`${template.design} preview`} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: '#fff', aspectRatio: '1 / 1.3', maxWidth: 220, mx: 'auto', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {header}
      <Box sx={{ p: 1.25, flex: 1 }}>
        <Typography sx={{ fontSize: 10, fontWeight: 700, color: colour, mb: 1 }}>{TEMPLATE_LABEL[template.documentType].toUpperCase()}</Typography>
        {line('80%')}{line('60%')}{line('90%')}{line('40%')}
        <Box sx={{ mt: 1.5, height: 30, bgcolor: colour, opacity: 0.1, borderRadius: 0.5 }} />
      </Box>
      <Box sx={{ px: 1.25, py: 0.75, fontSize: 8, color: 'text.secondary', borderTop: '1px solid', borderColor: 'divider', minHeight: 20 }}>
        {template.footerText || ' '}
      </Box>
    </Box>
  );
}
